<template>
  <div ref="contextualMenu" class="contextual-menu">
    <BaseRoundButton
      data-test="contextual-menu-tag"
      @mouseover.native="showTagModal()"
      :tooltip-text="$t('contextual-menu.tag-annotation')"
      :tooltip-subtitle="annotationRestrictedTooltip"
      tooltip-placement="left"
      :is-light="tagActive"
      :disabled="!canAnnotate"
      icon="tag"
      is-white
      :size="32"
    />
    <BaseRoundButton
      data-test="contextual-menu-highlight"
      @click.native="highlightAnnotation"
      @mouseover.native="hideSubmenus"
      :tooltip-text="$t('contextual-menu.highlight-annotation')"
      :tooltip-subtitle="annotationRestrictedTooltip"
      tooltip-placement="left"
      :disabled="!canAnnotate"
      icon="highlighter"
      is-white
      v-show="selectedAnnotation == null"
      :size="32"
    />
    <BaseRoundButton
      data-test="contextual-menu-task"
      @mouseover.native="showTaskModal()"
      :tooltip-text="$t('contextual-menu.link-to-task')"
      :tooltip-subtitle="annotationRestrictedTooltip"
      tooltip-placement="left"
      :is-light="taskModalActive"
      :disabled="!canAnnotate"
      icon="clipboard"
      is-white
      :size="32"
    />
    <BaseRoundButton
      ref="colorMenuTrigger"
      data-test="contextual-menu-color"
      @mouseover.native="onColorMenuHover"
      :tooltip-text="$t('contextual-menu.annotation-color')"
      :tooltip-subtitle="annotationRestrictedTooltip"
      tooltip-placement="left"
      :is-light="colorModalActive"
      :disabled="!canAnnotate"
      icon="droplet"
      is-white
      :size="32"
    />
    <BaseRoundButton
      data-test="contextual-menu-copy"
      @click.native="copySelectedText"
      @mouseover.native="hideSubmenus"
      :tooltip-text="$t('contextual-menu.copy-selected-text')"
      :tooltip-subtitle="copyRestrictedTooltip"
      tooltip-placement="left"
      :disabled="!canCopy"
      icon="copy"
      is-white
      :size="32"
    />
    <BaseRoundButton
      data-test="contextual-menu-delete"
      @click.native="deleteAnnotation"
      @mouseover.native="hideSubmenus"
      :tooltip-text="$t('contextual-menu.delete-annotation')"
      tooltip-placement="left"
      icon="trash-2"
      is-white
      v-show="selectedAnnotation !== null"
      :size="32"
    />
    <ConfirmationDialog
      v-if="selectedAnnotation"
      ref="confirmationAnnotationDialog"
      :buttonText="$t('actions.delete')"
      @execute="executeDeleteLinkedAnnotation"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="page.opportunity.delete-annotation-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
          </i18n>
          <div class="confirmation-dialog__annotation">
            <span class="confirmation-dialog__annotation--text">
              <BaseIcon name="link-2" class="base-connection-tag__icon" v-if="isLinkedAnnotation" />
              {{ selectedAnnotation.text }}
            </span>
          </div>
          <div class="dialog__warning-notice" v-if="isLinkedAnnotation">
            {{ $t('page.opportunity.delete-annotation-warning') }}
          </div>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseRoundButton from '@/components/base/BaseRoundButton';
import DebounceMixin from '@/mixins/debounce-mixin';
import { mapActions, mapGetters } from 'vuex';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import TextAnnotatorMixin from '@/mixins/text-annotator-mixin';
import ContextualMenuMixin from '@/mixins/contextual-menu-mixin';
import { canAnnotateDocument, canCopyDocument } from '@/util/document-permissions-util';

export default {
  name: 'ContextualMenu',
  components: {
    BaseIcon,
    ConfirmationDialog,
    BaseRoundButton,
  },
  mixins: [TextAnnotatorMixin, DebounceMixin, ContextualMenuMixin],
  props: {
    tagActive: {
      type: Boolean,
      default: false,
    },
    taskModalActive: {
      type: Boolean,
      default: false,
    },
    colorModalActive: {
      type: Boolean,
      default: false,
    },
    annotationToHighlight: {
      type: Object,
    },
    opportunityId: String,
    documentId: String,
  },
  computed: {
    ...mapGetters('annotations', ['selectedAnnotation', 'linkedAnnotations']),
    ...mapGetters('opportunityDocuments', ['opportunityDocument']),
    canCopy() {
      return canCopyDocument(this.opportunityDocument);
    },
    canAnnotate() {
      return canAnnotateDocument(this.opportunityDocument);
    },
    // Empty string means no subtitle
    annotationRestrictedTooltip() {
      return this.canAnnotate ? '' : this.$t('contextual-menu.annotation-restricted');
    },
    copyRestrictedTooltip() {
      return this.canCopy ? '' : this.$t('contextual-menu.copy-restricted');
    },
    isLinkedAnnotation() {
      return (
        this.linkedAnnotations.find((annotation) => annotation.textAnnotationId === this.selectedAnnotation.id) !==
        undefined
      );
    },
  },
  methods: {
    ...mapActions('evaluationGrid', ['getEvaluationGrid']),
    ...mapActions('annotations', ['deleteDocumentAnnotation', 'resetSelectedAnnotation']),

    closeMenu() {
      this.$emit('click-outside');
    },
    showTagModal() {
      // Handlers are bound with `.native` on the wrapper, so `:disabled` alone does not stop them.
      if (!this.canAnnotate) {
        this.hideSubmenus();
        return;
      }
      this.$emit('show-tag-modal');
    },
    hideTagModal() {
      this.$emit('hide-tag-modal');
    },
    showTaskModal() {
      // Linking to a task creates an annotation as its citation anchor, so it follows the same gate.
      if (!this.canAnnotate) {
        this.hideSubmenus();
        return;
      }
      this.$emit('show-task-modal');
    },
    hideTaskModal() {
      this.$emit('hide-task-modal');
    },
    hideColorModal() {
      this.$emit('hide-color-modal');
    },
    hideSubmenus() {
      this.hideTagModal();
      this.hideTaskModal();
      this.hideColorModal();
    },
    onColorMenuHover() {
      if (!this.canAnnotate) {
        this.hideSubmenus();
        return;
      }
      this.showColorModal();
      const contextualMenuRect = this.$refs.contextualMenu.getBoundingClientRect();
      const triggerElement = this.$refs.colorMenuTrigger?.$el || this.$refs.colorMenuTrigger;
      if (!triggerElement) return;
      const triggerRect = triggerElement.getBoundingClientRect();
      this.$emit('set-color-menu-position', {
        top: triggerRect.top - contextualMenuRect.top,
        left: triggerRect.right - contextualMenuRect.left + 8,
      });
    },
    showColorModal() {
      if (!this.canAnnotate) return;
      this.$emit('show-color-modal');
    },
    copySelectedText() {
      if (!this.canCopy) return;
      let selectedText;

      if (this.selectedAnnotation && this.selectedAnnotation.text.trim().length > 0) {
        selectedText = this.selectedAnnotation.text;
      } else if (this.annotationToHighlight) {
        selectedText = this.annotationToHighlight.text;
      }
      if (!selectedText) {
        this.$toast.error(this.$t('page.analysis.text-selection.copy.no-text'));
        return;
      }
      navigator.clipboard
        .writeText(selectedText)
        .then(() => {
          this.$toast.success(this.$t('page.analysis.text-selection.copy.success'));
          this.removeAnnotation();
        })
        .catch(() => {
          this.$toast.error(this.$t('page.analysis.text-selection.copy.error'));
        });
    },
    removeAnnotation() {
      if (this.textSelection && !this.annotationToHighlight) {
        this.deleteAnnotation(true);
        this.textSelection = false;
      }
    },
    deleteAnnotation(noToast) {
      if (this.isLinkedAnnotation) {
        this.$refs.confirmationAnnotationDialog.openDialog();
      } else {
        this.executeDeleteAnnotation(noToast);
      }
      this.hideTagModal();
    },
    async executeDeleteLinkedAnnotation() {
      await this.executeDeleteAnnotation();
      this.getEvaluationGrid(this.opportunityId);
    },
    async executeDeleteAnnotation(noToast) {
      // Delete the highlight effect
      this.removeHighlightRange(this.selectedAnnotation);
      // Then delete the annotation

      const { opportunityDocumentId } = this.selectedAnnotation;

      await this.deleteDocumentAnnotation({
        opportunityId: this.opportunityId,
        docId: opportunityDocumentId ? opportunityDocumentId : this.documentId,
        id: this.selectedAnnotation.id,
      })
        .then(() => {
          this.resetSelectedAnnotation();
          if (!noToast) {
            this.$toast.success(this.$t('toast.success'));
          }
          this.closeMenu();
        })
        .catch((error) => {
          this.resetSelectedAnnotation();
          this.$toast.error(error);
        });
    },
    highlightAnnotation() {
      if (!this.canAnnotate) return;
      this.$emit('highlight-annotation');
    },
  },
};
</script>

<style lang="scss">
// TODO: Revoir css
.contextual-menu {
  display: flex;
  flex-direction: column;
  background-color: $color-white;
  color: $color-normal;
  border-radius: $border-radius-root;
  padding: 4px;
  box-shadow: $shadow-tooltip;
  z-index: 5;
  transition: color 200ms ease;
}
</style>
